import React, { cloneElement, isValidElement, ReactElement } from 'react';
import { css } from '@emotion/react';
import classNames from 'classnames';
import {
  button_base_css,
  button_icon_size_css,
  getIconButtonKind,
  getIconButtonStyle,
  icon_button_size_css,
} from './styles';
import { IconButtonShape, IconButtonKind, IconButtonSize } from './types';
import { ButtonProps } from './Button';
import { IconProps } from '../icons/generated';
import { Slot, Slottable } from '@radix-ui/react-slot';

export interface IconButtonProps
  extends Omit<ButtonProps, 'loading' | 'startIcon' | 'endIcon' | 'kind' | 'shape' | 'size'> {
  /**
   * shape 속성이 'circle'의 경우 적용되지 않습니다.
   * @default 'outlined_secondary'
   */
  kind?: IconButtonKind;
  /**
   * 렌더링 될 아이콘 요소
   *  - Icon 컴포넌트 color props가 없다면 currentColor가 지정됩니다.
   */
  icon?: ReactElement<IconProps>;
  /** @default 'medium' */
  size?: IconButtonSize;
  /** @default 'rectangle' */
  shape?: IconButtonShape;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      type = 'button',
      shape = 'rectangle',
      kind = 'outlined_secondary',
      size = 'medium',
      fill,
      disabled,
      className,
      icon,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const iconSize = button_icon_size_css[size];
    const _icon = isValidElement(icon)
      ? cloneElement(icon, { color: icon.props.color ?? 'currentColor', size: iconSize })
      : null;
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        type={type}
        className={classNames('pds-button', className)}
        css={[
          button_base_css,
          getIconButtonStyle(shape as IconButtonShape),
          shape === 'rectangle' && getIconButtonKind(kind as IconButtonKind),
          icon_button_size_css[size],
          css`
            ${!fill && 'flex: none;'}
            ${fill && 'width: 100%;'}
            ${size === 'xxsmall' && shape === 'rectangle' && 'border-radius: 6px;'}
            cursor: ${disabled ? 'not-allowed' : 'pointer'};
          `,
        ]}
        disabled={disabled}
        {...props}
        ref={ref}
      >
        <Slottable>{_icon}</Slottable>
      </Comp>
    );
  },
);
